import { deepSignal } from 'deepsignal';
import { CHARITY } from '@util/const.js';
import CustomizeSection from '@components/overlays/shared/customize-section.jsx';
import LinkSetup from '@components/overlays/social/link-setup/link-setup.jsx';
import Picker from '@components/input/picker.input.jsx';
import ColorPicker from '@components/pickers/color-picker/color.picker.jsx';
import ScaledOverlayWrapper from '@components/overlays/wrapper/scaled-overlay-wrapper.jsx';
import CharitySection from '@components/shared/charity-section.jsx';
import {
  toVerticalProgressBarBackgroundColor,
  VerticalProgressBar,
} from '@playlive/overlay-core/progress-bars/vertical';
import { WandMagicSparkles } from 'flowbite-react-icons/solid';
import HorizontalCustomizeWrapper from '@components/overlays/wrapper/horizonal-customize-wrapper.jsx';
import useCommon from '@hooks/useCommon.js';
import ColorPickerLabel from '@components/pickers/color-picker/color-picker-label.jsx';
import YesNoButtonInput from '@components/input/yes-no-button.input.jsx';
import { charityIsTiltify, isYes } from '@util/common.js';
import { useEffect } from 'preact/hooks';
import useAdjustableAmounts from '@hooks/useAdjustableAmounts.js';

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  bgColor: 'default-red',
  verticalType: 'rounded',
  showOriginalGoal: 'no',
  ccamMode: 'no',
});

const useBetaProgressBars = isYes(
  import.meta.env.VITE_BETA_PROGRESS_BARS || 'no'
);
const urlBase = useBetaProgressBars ? 'progress-bars/vertical' : 'vertical';

export default function VerticalProgressBarPage() {
  const { overlayInfo, overlayURL, setConfigState } = useCommon({
    config,
    urlBase,
  });

  const { currentAmount, goalAmount, updateAmounts, setCurrentAmount } =
    useAdjustableAmounts();

  const themes = [
    { name: '3D', value: '3d' },
    { name: 'Slanted', value: 'slanted' },
    { name: 'Rounded', value: 'rounded' },
  ];

  const triggerGoalMetAnimation = () => {
    setCurrentAmount(1000);
    setTimeout(() => {
      setCurrentAmount(750);
    }, 10000);
  };

  function filterColors(color) {
    return !color.includes('grey');
  }

  useEffect(() => {
    if (isYes(config.showOriginalGoal)) {
      setCurrentAmount(1900);

      updateAmounts({
        newCurrentAmount: 1900,
        newGoalAmount: 2000,
      });
    } else {
      updateAmounts({
        newCurrentAmount: 750,
        newGoalAmount: 1000,
      });
    }
  }, [config.showOriginalGoal]);

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col xl:flex-row overflow-x-hidden">
        <div className="example_overlay relative w-full">
          <ScaledOverlayWrapper
            defaultScale={'scale-60'}
            allowScaling={false}
            innerClassName={'h-[550px]'}
            addPadding={config.verticalType !== '3d'}
            addPosition={
              config.verticalType === '3d' ? 'absolute bottom-0' : ''
            }
          >
            <div
              className={` ${config.verticalType === '3d' && 'absolute bottom-0 h-full'} max-w-full w-full`}
            >
              <VerticalProgressBar
                goalAmount={goalAmount}
                currentAmount={currentAmount}
                showOriginalGoal={isYes(config.showOriginalGoal)}
                ccamMode={isYes(config.ccamMode)}
                backgroundColor={
                  isYes(config.ccamMode)
                    ? toVerticalProgressBarBackgroundColor('light-yellow')
                    : toVerticalProgressBarBackgroundColor(config.bgColor)
                }
                type={config.verticalType}
                className={`${config.verticalType === '3d' && 'absolute bottom-[-250px] left-0 right-0'}`}
                originalGoalAmount={0}
              />
            </div>
          </ScaledOverlayWrapper>
          <div
            className="absolute bottom-2 left-2 z-50 tooltip tooltip-right"
            data-tip="Demo Goal Met Animation"
          >
            <button
              disabled={currentAmount >= 1000}
              onClick={triggerGoalMetAnimation}
              className="btn btn-circle btn-sm btn-neutral bg-white hover:bg-auto border-0 text-secondary mt-2"
            >
              <WandMagicSparkles />
            </button>
          </div>
        </div>

        <HorizontalCustomizeWrapper className="flex flex-col mb-0!">
          <CharitySection
            onChange={setConfigState}
            charityType={config.charityType}
          />

          <CustomizeSection className="flex-grow" title="Appearance">
            <ColorPickerLabel
              color={config.bgColor}
              labelText="Progress bar"
              className={
                isYes(config.ccamMode)
                  ? 'opacity-50 transition-opacity'
                  : 'opacity-100 transition-opacity'
              }
            />
            <ColorPicker
              disabled={isYes(config.ccamMode)}
              currentColor={config.bgColor}
              fieldName="bgColor"
              filterFunction={filterColors}
              onChange={setConfigState}
            />

            <Picker
              onChange={setConfigState}
              currentValue={config.verticalType}
              items={themes}
              fieldName="verticalType"
              labelText="Theme"
            />

            <YesNoButtonInput
              className="flex-1"
              onChange={setConfigState}
              mode="checkbox"
              currentValue={config.ccamMode}
              name="ccamMode"
              label="CCAM Mode"
              labelNoClass="text-black"
              buttonNoClass="bg-warning opacity-60 shadow-none"
              buttonYesClass="bg-warning opacity-100 shadow-none"
              labelYesClass="text-black"
            />

            <div className="my-2">
              <div className="flex flex-col md:flex-row mt-2">
                <YesNoButtonInput
                  className="md:mr-4 mb-2 md:mb-0 flex-1"
                  onChange={setConfigState}
                  mode="checkbox"
                  currentValue={config.showOriginalGoal}
                  name="showOriginalGoal"
                  label="Show Original Goal"
                  labelTextClassName="text-white"
                  hidden={!charityIsTiltify(config.charityType)}
                />
              </div>

              <p className="pl-1 pt-1 text-gray-500">
                When enabled, the progress bar extends beyond the original goal
                to show both completed progress and the remaining distance to
                the new goal
              </p>
            </div>
          </CustomizeSection>
        </HorizontalCustomizeWrapper>
      </div>

      <LinkSetup
        allowTeamCampaignSelection={true}
        constrainWidth={false}
        charityType={config.charityType}
        obsDimensions={overlayInfo.obsDimensions}
        overlayName={overlayInfo.name}
        overlayURL={overlayURL}
      />
    </div>
  );
}
